import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import PageBackButton from '../../components/PageBackButton';
import { parentMeetingsAPI, rmAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  formatLocalYMD,
  getTodayYMDInTimezone,
  getWeekStartInTimezone,
  localDateFromYmd,
} from '../../utils/dateTimeUtils';
import { INDIA_TZ } from '../../utils/parentMeetingTime';
import { addCalendarDaysYmd } from '../Meetings/meetingListUtils';
import '../../components/Teacher/TeacherWeeklyTimetable.css';

function tileTitle(meeting) {
  const batches = [...new Set((meeting.students || []).map((student) => student.batchName).filter(Boolean))];
  return batches.join(' · ') || meeting.title || 'Parent meeting';
}

function tileSubtitle(meeting) {
  return (meeting.students || [])
    .map((student) => (student.country ? `${student.name} (${student.country})` : student.name))
    .join(', ');
}

export default function ParentMeetingCalendar() {
  const navigate = useNavigate();
  const { isAdmin, isSalesCountryScope } = useAuth();
  const canFilterRm = isAdmin() || isSalesCountryScope();
  const calendarRef = React.useRef(null);
  const [weekStart, setWeekStart] = useState(() => getWeekStartInTimezone(INDIA_TZ));
  const [rmUserId, setRmUserId] = useState('');
  const [selected, setSelected] = useState(null);
  const weekEnd = addCalendarDaysYmd(weekStart, 6);

  const candidatesQuery = useQuery(
    ['rm-candidates'],
    async () => {
      const res = await rmAPI.getCandidates();
      return res.data?.data || [];
    },
    { enabled: canFilterRm }
  );

  const calendarQuery = useQuery(
    ['parent-meeting-calendar', weekStart, weekEnd, canFilterRm ? rmUserId : ''],
    async () => {
      const params = { from: weekStart, to: weekEnd };
      if (canFilterRm && rmUserId) params.rmUserId = rmUserId;
      const res = await parentMeetingsAPI.calendar(params);
      return res.data?.data || [];
    },
    { keepPreviousData: true }
  );

  const events = useMemo(
    () => (calendarQuery.data || []).map((meeting) => ({
      id: meeting.id,
      title: tileTitle(meeting),
      start: `${meeting.meetingDate}T${meeting.startTime}:00`,
      end: `${meeting.meetingDate}T${meeting.endTime}:00`,
      extendedProps: {
        meeting,
        subtitle: tileSubtitle(meeting),
      },
    })),
    [calendarQuery.data]
  );

  return (
    <Box>
      <PageBackButton />
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h5">Parent meeting calendar</Typography>
          <Typography variant="body2" color="text.secondary">
            Weekly view of scheduled parent meetings. The grid uses India time ({INDIA_TZ}). Each meeting shows the batch, student, and country.
          </Typography>
        </Box>
        <Button onClick={() => navigate('/rm/parent-meetings')}>Meeting list</Button>
      </Stack>

      <Card className="teacher-weekly-timetable">
        <CardContent>
          {canFilterRm ? (
            <TextField
              select
              size="small"
              label="RM"
              value={rmUserId}
              onChange={(event) => setRmUserId(event.target.value)}
              sx={{ minWidth: 240, mb: 2 }}
            >
              <MenuItem value="">All RMs</MenuItem>
              {(candidatesQuery.data || []).map((rm) => (
                <MenuItem key={rm.userId} value={rm.userId}>
                  {rm.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          <Box sx={{ position: 'relative' }}>
            {calendarQuery.isFetching ? (
              <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                <CircularProgress size={18} />
              </Box>
            ) : null}
            <FullCalendar
              ref={calendarRef}
              plugins={[timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              initialDate={localDateFromYmd(weekStart)}
              firstDay={1}
              weekends
              height="auto"
              expandRows
              nowIndicator={false}
              allDaySlot={false}
              slotMinTime="00:00:00"
              slotMaxTime="24:00:00"
              slotDuration="00:30:00"
              scrollTime="08:00:00"
              customButtons={{
                indiaToday: {
                  text: 'today',
                  click: () => {
                    const ymd = getTodayYMDInTimezone(INDIA_TZ);
                    calendarRef.current?.getApi()?.gotoDate(localDateFromYmd(ymd));
                  },
                },
              }}
              headerToolbar={{
                left: 'prev,next indiaToday',
                center: 'title',
                right: '',
              }}
              titleFormat={{ year: 'numeric', month: 'short', day: 'numeric' }}
              dayHeaderContent={(arg) => ({ html: dayjs(arg.date).format('ddd D MMM') })}
              events={events}
              datesSet={(arg) => {
                const ymd = formatLocalYMD(arg.start);
                if (ymd && ymd !== weekStart) setWeekStart(ymd);
              }}
              eventClick={(info) => setSelected(info.event.extendedProps?.meeting || null)}
              eventContent={(arg) => (
                <div
                  className="teacher-weekly-timetable__event"
                  style={{
                    background: '#e8f1ff',
                    color: '#1656c2',
                    border: '1px solid #cfe0ff',
                    borderRadius: 10,
                  }}
                >
                  <div className="teacher-weekly-timetable__event-title">{arg.event.title}</div>
                  {arg.event.extendedProps?.subtitle ? (
                    <div className="teacher-weekly-timetable__event-subtitle">
                      {arg.event.extendedProps.subtitle}
                    </div>
                  ) : null}
                </div>
              )}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{selected?.title || 'Parent meeting'}</DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <Stack spacing={1}>
              {(selected.students || []).map((student) => (
                <Typography key={student.studentId} variant="body2">
                  {[student.batchName, student.name, student.country].filter(Boolean).join(' · ')}
                  {student.teacherName ? ` · Teacher ${student.teacherName}` : ''}
                </Typography>
              ))}
              <Typography variant="body2">India: {selected.clocks?.india?.label}</Typography>
              {(selected.clocks?.parents || []).map((parent) => (
                <Typography key={parent.timezone} variant="body2">
                  Parent ({(parent.names || []).join(', ')}): {parent.label}
                </Typography>
              ))}
              {selected.rmName ? <Typography variant="body2">RM: {selected.rmName}</Typography> : null}
              {selected.agenda ? <Typography variant="body2">Agenda: {selected.agenda}</Typography> : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
          {selected?.joinUrl ? (
            <Button href={selected.joinUrl} target="_blank" rel="noreferrer">Join</Button>
          ) : null}
          <Button variant="contained" onClick={() => navigate('/rm/parent-meetings')}>
            Open list
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
